import React, { useCallback, useState } from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { Alert, Box, Link, Spinner, Text, useAsync } from '@wix/cli-ui-kit';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { VibeRepositorySetupPolling } from '@wix/headless-command';
import { useVeloClient } from '@wix/velo-client';
import { useDevCenterClient, ComponentType } from '@wix/dev-center-client';
import { useProjectModel } from '../../model.js';
import { PlatformLink } from '../PlatformLink/PlatformLink.js';
import { backendWorkerComponentSchema } from './schemas.js';

export const ConnectWixVibeCommand: FC = () => {
  const {
    model: { projectFolder, config },
  } = useProjectModel();

  const { getGitHubOnboardingStatus } = useVeloClient();
  const { getAppByVersion } = useDevCenterClient();
  const [isConnected, setIsConnected] = useState(false);

  const { status: connectionStatus } = useAsync(async () => {
    if (config.projectType !== 'Site') {
      throw new CliError({
        code: CliErrorCode.ConnectUnsupportedForProjectType(),
        cause: null,
      });
    }

    return {
      existingConnection: await getGitHubOnboardingStatus(config.appId),
      siteId: config.siteId,
    };
  }, []);

  const getDeploymentUrl = useCallback(async () => {
    const { data } = await getAppByVersion({ appId: config.appId });
    const backendWorkerComponent = data.components.find(
      (component) => component.compType === ComponentType.BACKEND_WORKER
    );

    const parsedBackendWorkerComponent = backendWorkerComponentSchema.safeParse(
      backendWorkerComponent
    );

    if (parsedBackendWorkerComponent.success) {
      return parsedBackendWorkerComponent.data.compData.backendWorker
        .deploymentUrl;
    }
  }, [getAppByVersion, config.appId]);

  const { status: deploymentUrlStatus } = useAsync(async () => {
    if (!isConnected) {
      return;
    }
    const deploymentUrl = await getDeploymentUrl();

    return deploymentUrl;
  }, [isConnected, config.appId]);

  return match(connectionStatus, {
    Error: () => null,
    Loading: () => <Spinner text="Checking Wix Vibe connection status..." />,
    Success: ({ result: { existingConnection, siteId } }) => {
      if (existingConnection) {
        return (
          <Box flexDirection="column" gap={1} marginTop={1}>
            <Alert type="insight">
              <Text>
                This project is already connected to Wix Vibe.{' '}
                <Link url={existingConnection.htmlUrl}>View Repository</Link>.
              </Text>
            </Alert>
          </Box>
        );
      }

      return (
        <Box flexDirection="column" marginTop={1}>
          <VibeRepositorySetupPolling
            projectId={config.appId}
            projectFolder={projectFolder}
            onSubmit={() => setIsConnected(true)}
          />
          {isConnected && (
            <Box gap={1} flexDirection="column" marginBottom={1}>
              <Text>You can edit your site in the Wix Vibe Editor now.</Text>
              <Box paddingLeft={2} flexDirection="column">
                <PlatformLink
                  url={`https://vibe.wix.com/projects/${siteId}`}
                  platform="editor"
                />
                <PlatformLink
                  url={`https://manage.wix.com/dashboard/${siteId}`}
                  platform="dashboard"
                />
                {match(deploymentUrlStatus, {
                  Success: ({ result }) =>
                    result ? (
                      <PlatformLink url={result} platform="site" />
                    ) : null,
                  Loading: () => null,
                  Error: () => null,
                })}
              </Box>
            </Box>
          )}
        </Box>
      );
    },
  });
};
